'use client';

import { Hit as AlgoliaHit } from 'instantsearch.js/es/types';
import { Icon, LinkWrapper, Picture, Skeleton, TextButton, Typography } from '@workspace/ui/common';
import { SearchAllHit } from '@workspace/interfaces';
import { forceDownload, getSafeFilename } from '@workspace/lib/pdf-utils';
import { handleLinkClick } from '@workspace/hooks';
import { cn } from '@workspace/lib';

interface SearchPageItemHitProps {
  hit: AlgoliaHit<SearchAllHit>;
  className?: string;
}

export const mapAlgoliaSearchTypeToIcon = (type: string) => {
  switch (type) {
    case 'documentFile':
      return 'FileBend';
    case 'article':
      return 'EditList';
    case 'product':
    case 'structaProductLevel1':
    case 'structaProductLevel2':
    case 'structaProductLevel3':
    case 'productsList':
      return 'LayoutThird';
    default:
      return 'BrowserIcon';
  }
};

export function SearchPageItemHit({ hit, className }: SearchPageItemHitProps) {
  const { title, _type, fileUrl, thumbnailImage: image } = hit;
  const description = hit.description || hit.productSummary || hit.content;

  const onDownload = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!fileUrl) return;

    await forceDownload(fileUrl, getSafeFilename(title || undefined));
  };

  const divClickHandler = (e: React.MouseEvent) => {
    if (hit._type === 'documentFile' && hit.fileUrl) {
      e.preventDefault();
      handleLinkClick(
        {
          type: 'external',
          url: fileUrl,
          label: 'View',
        },
        e,
      );
    }
  };

  return (
    <LinkWrapper link={hit.url || hit.fileUrl} onClick={divClickHandler}>
      <div
        className={cn(
          'flex bg-white hover:bg-secondary-50 rounded-md transition-colors border border-secondary-100 px-sm py-xs md:py-sm justify-between gap-xs md:gap-lg',
          className,
        )}
      >
        <div className="flex-1 flex gap-sm">
          <div className="size-md">
            <Icon name={mapAlgoliaSearchTypeToIcon(_type)} size={24} />
          </div>
          <div className="flex flex-col flex-1 justify-between">
            <div>
              {title && (
                <Typography variant="bodyMd" className="line-clamp-2 font-bold text-sm mb-1">
                  {title}
                </Typography>
              )}
              {hit._type !== 'documentFile' && description && (
                <Typography variant="bodySm" className="line-clamp-3 text-black text-xs">
                  {description}
                </Typography>
              )}
            </div>
            {fileUrl && (
              <TextButton
                className={cn('underline w-fit !text-base font-semibold !text-sans !text-primary')}
                onClick={onDownload}
              >
                Download
              </TextButton>
            )}
          </div>
        </div>
        <div className="relative w-16 h-22.5 flex-shrink-0 bg-light-silver md:ml-sm flex items-center justify-center">
          {image ? (
            <Picture
              src={image}
              alt={title || 'Image'}
              desktopWidth={64}
              desktopHeight={90}
              imgClassName="object-cover"
              fill
            />
          ) : _type === 'documentFile' ? (
            <Icon
              name="FileBend"
              size={28}
              color="var(--color-raisin-black)"
              className="pointer-events-none select-none"
            />
          ) : null}
        </div>
      </div>
    </LinkWrapper>
  );
}

export const SearchPageItemHitSkeleton = () => {
  return (
    <div className="flex bg-white rounded-md border border-secondary-100 px-sm py-xs md:py-sm justify-between gap-xs md:gap-lg">
      <div className="flex-1 flex gap-sm">
        <div className="size-md">
          <Skeleton className="w-full h-full" />
        </div>
        <div className="flex flex-col flex-1 justify-between">
          <div>
            <Skeleton className="w-3/4 h-4 mb-1" />
            <Skeleton className="w-full h-3" />
          </div>
          <Skeleton className="w-1/4 h-4" />
        </div>
      </div>
      <div className="relative w-16 h-22.5 flex-shrink-0 bg-light-silver md:ml-sm">
        <Skeleton className="w-full h-full" />
      </div>
    </div>
  );
};
